import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Key used to remember the customer's click & collect store between visits.
 * Mirrors the cart attributes written on confirm so the cart and the PDP agree.
 */
const STORAGE_KEY = 'wilson:click-and-collect-store';
const CART_ATTRIBUTE = '_pickup_store_name';
const CART_ATTRIBUTE_STORE_ID = '_pickup_store_id';

/**
 * @typedef {object} SelectedStore
 * @property {string} id - Shopify location id.
 * @property {string} name - Store display name.
 */

/**
 * Reads the remembered store, tolerating a cleared or corrupt storage entry.
 * @returns {SelectedStore | null}
 */
function readSelectedStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.id ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * @param {SelectedStore | null} store
 */
function writeSelectedStore(store) {
  try {
    if (store) localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage can be unavailable in private browsing — the block still works,
    // the choice simply is not remembered.
  }
}

/**
 * Intercepts the add to bag button while it is asking for a store. Registered
 * once for the page so it survives the buy buttons being re-rendered.
 */
let addToCartInterceptRegistered = false;

function registerAddToCartIntercept() {
  if (addToCartInterceptRegistered) return;
  addToCartInterceptRegistered = true;

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const button = target.closest('[data-store-availability-needs-store="true"]');
      if (!button) return;

      const blockId = button.getAttribute('data-store-availability-block');
      const block = blockId ? document.getElementById(blockId) : null;
      if (!(block instanceof StoreAvailability)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      block.openStoreSelector();
    },
    { capture: true }
  );
}

/**
 * Store availability / Click & Collect block.
 *
 * Owns three views (main, in-store only, fully out of stock), the tab state,
 * the two drawers and — optionally — the label of the add to bag button.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [viewMain]
 * @property {HTMLElement} [viewInStoreOnly]
 * @property {HTMLElement} [viewOutOfStock]
 * @property {HTMLElement} [tabs]
 * @property {HTMLElement} [ccIconEmpty]
 * @property {HTMLElement} [ccIconInStock]
 * @property {HTMLElement} [ccIconOutOfStock]
 * @property {HTMLElement} [ccHeading]
 * @property {HTMLElement} [ccNote]
 * @property {HTMLElement} [ccAction]
 * @property {HTMLElement} [ccActionIcon]
 * @property {HTMLElement} [ccActionLabel]
 * @property {HTMLElement} [ccActionChevron]
 * @property {HTMLElement} [storeSelector]
 * @property {HTMLElement} [inStoreDrawer]
 *
 * @extends Component<Refs>
 */
class StoreAvailability extends Component {
  /** @type {'delivery' | 'click-collect'} */
  #activeTab = 'delivery';

  /** @type {SelectedStore | null} */
  #selectedStore = null;

  /** @type {string | null} */
  #pendingStoreId = null;

  /** @type {AbortController | undefined} */
  #activeFetch;

  /** @type {Element | null} */
  #section = null;

  /** @type {{ccEmptyHeading: string, ccEmptyNote: string, selectStoreLabel: string} | null} */
  #defaultCopy = null;

  /** @type {any} */
  #datajetData = null;

  /** @type {Promise<any> | null} */
  #datajetPromise = null;

  connectedCallback() {
    super.connectedCallback();

    registerAddToCartIntercept();

    this.#selectedStore = readSelectedStore();
    this.#activeTab =
      this.#deliveryAvailable && this.#hasTab('delivery')
        ? 'delivery'
        : this.#ccAvailable && this.#hasTab('click-collect')
          ? 'click-collect'
          : 'delivery';

    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.#section?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);

    this.#refresh();

    this.#fetchDatajetStock().then(() => {
      this.#applyDatajetStock();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.#section?.removeEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.#restoreAddToCartButton();
  }

  /**
   * Runs after the Section Rendering API swaps this element's contents.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#refresh();
  }

  /**
   * Re-reads the Liquid-rendered copy, rewires the drawer list, then paints.
   */
  #refresh() {
    this.#defaultCopy = null;
    this.#captureDefaults();
    this.#bindSelectorList();
    this.render();
    if (this.#datajetData) {
      this.#applyDatajetStock();
    }
  }

  async #fetchDatajetStock() {
    const queryId = this.dataset.stockDisplayQueryId;
    const productId = this.dataset.productId;
    if (!queryId || !productId) return;

    if (this.#datajetData) return this.#datajetData;
    if (this.#datajetPromise) return this.#datajetPromise;

    const url = `/apps/datajet/task/${queryId}`;
    const payload = { productId: String(productId) };

    this.#datajetPromise = fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Datajet network error');
        return response.json();
      })
      .then((responseJson) => {
        if (responseJson?.data?.product) {
          this.#datajetData = responseJson.data;
          return this.#datajetData;
        }
        throw new Error('Invalid Datajet response structure');
      })
      .catch((error) => {
        console.error('Datajet fetch error:', error);
        return null;
      })
      .finally(() => {
        this.#datajetPromise = null;
      });

    return this.#datajetPromise;
  }

  #applyDatajetStock() {
    if (!this.#datajetData) return;

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    const normalizeVariantGid = (id) => {
      const s = String(id);
      if (s.startsWith('gid://shopify/ProductVariant/')) return s;
      return `gid://shopify/ProductVariant/${s}`;
    };

    const normalizeLocationGid = (id) => {
      const s = String(id).replace('gid://shopify/Location/', '');
      return `gid://shopify/Location/${s}`;
    };

    const variantEdge = this.#datajetData.product.variants.edges.find(({ node }) => (
      normalizeVariantGid(node.id) === normalizeVariantGid(variantId)
    ));

    if (!variantEdge) return;

    const variant = variantEdge.node;
    const hideStockDisplay = variant.hideStockDisplay?.value === 'true';

    const blockClickCollect = this.dataset.hasCcTab !== 'true';

    const stores = {};
    let totalWarehouseAvailable = 0;
    let totalStoreAvailable = 0;

    variant.inventoryItem.inventoryLevels.edges.forEach(({ node: level }) => {
      const loc = level.location;
      const locationId = normalizeLocationGid(loc.id);
      const numericLocationId = locationId.replace('gid://shopify/Location/', '');

      const availableQuantity = level.quantities.find(q => q.name === 'available')?.quantity ?? 0;

      const isWarehouse = loc.isWarehouse?.value === 'true';
      const storeHidden = loc.storeHidden?.value === 'true';
      const enableForPickup = loc.enableForPickup?.value === 'true';
      const storeEnable = loc.storeStockAvailability?.value === 'true';
      const warehouseEnable = loc.warehouseStockAvailability?.value === 'true';

      if (warehouseEnable) {
        totalWarehouseAvailable += availableQuantity;
      }
      if (storeEnable && !isWarehouse && !storeHidden) {
        totalStoreAvailable += availableQuantity;
      }

      stores[numericLocationId] = {
        inStock: availableQuantity > 0 && storeEnable && !isWarehouse && !storeHidden,
        pickupEnabled: enableForPickup && !blockClickCollect,
        pickupTime: this.dataset.textCcReadyTime ?? 'Ready in 2 hours',
        quantity: availableQuantity,
      };
      if (hideStockDisplay) {
        stores[numericLocationId].inStock = false;
        stores[numericLocationId].pickupEnabled = false;
      }
    });

    const locations = this.#datajetData.locations?.edges || [];
    locations.forEach(({ node }) => {
      const locationId = normalizeLocationGid(node.id);
      const numericLocationId = locationId.replace('gid://shopify/Location/', '');
      if (!stores[numericLocationId]) {
        stores[numericLocationId] = {
          inStock: false,
          pickupEnabled: node.enableForPickup?.value === 'true' && !blockClickCollect,
          pickupTime: this.dataset.textCcReadyTime ?? 'Ready in 2 hours',
          quantity: 0,
        };
      }
    });

    const delivery = totalWarehouseAvailable > 0;
    const clickCollect = Object.values(stores).some(s => s.inStock && s.pickupEnabled);
    const inStore = Object.values(stores).some(s => s.inStock);

    this.setAvailability({
      delivery,
      clickCollect,
      inStore,
      stores,
    });
  }

  /* ------------------------------------------------------------------ *
   * Availability inputs
   * ------------------------------------------------------------------ */

  get #deliveryAvailable() {
    return this.dataset.deliveryAvailable === 'true';
  }

  get #ccAvailable() {
    return this.dataset.ccAvailable === 'true';
  }

  get #storeAvailable() {
    return this.dataset.storeAvailable === 'true';
  }

  /**
   * @param {'delivery' | 'click-collect'} tab
   */
  #hasTab(tab) {
    return tab === 'delivery' ? this.dataset.hasDeliveryTab === 'true' : this.dataset.hasCcTab === 'true';
  }

  /**
   * The view that matches the current availability.
   * @returns {'main' | 'in-store-only' | 'out-of-stock'}
   */
  get #view() {
    if (this.#deliveryAvailable || this.#ccAvailable) return 'main';
    if (this.#storeAvailable) return 'in-store-only';
    return 'out-of-stock';
  }

  /**
   * True only when this variant can be collected but not delivered: no delivery
   * stock (the Delivery tab is struck through), Click & Collect tab active, and the
   * confirmed store has it in stock with pickup enabled — the add to bag button
   * reads "Collect in store". Read at submit time by product-form.js and
   * racket-stringing.liquid to add the `_is_cc` property. A variant that can also
   * be delivered never gets it, even when the customer collects it.
   *
   * Delivery stock comes from the Datajet feed (#applyDatajetStock). Until it
   * lands, or if it fails, the Liquid fallback marks any in-stock variant as
   * deliverable, so the property is left off.
   * @returns {boolean}
   */
  get isClickAndCollect() {
    if (this.#view !== 'main' || this.#activeTab !== 'click-collect') return false;
    if (this.#deliveryAvailable) return false;
    if (!this.#ccAvailable || !this.#hasTab('click-collect')) return false;
    const card = this.#selectedStoreCard();
    return card?.dataset.storeInStock === 'true' && card?.dataset.storePickupEnabled === 'true';
  }

  /**
   * Integration point for a per-location stock feed.
   *
   * Shopify Liquid only exposes a per-location `available` flag for pickup
   * locations, so it cannot tell "the warehouse is empty but a store has one"
   * apart from "everything is in stock". A feed that knows real per-location
   * quantities (for example the location metafields `custom.is_default_warehouse`
   * and `custom.enable_for_pickup`) can push the resolved channel availability
   * here and the block will repaint into the matching view.
   *
   * @param {object} availability
   * @param {boolean} [availability.delivery] - Stock at the ecommerce location.
   * @param {boolean} [availability.clickCollect] - Stock at any pickup store.
   * @param {boolean} [availability.inStore] - Stock at any store.
   * @param {Record<string, {inStock?: boolean, pickupEnabled?: boolean, pickupTime?: string, quantity?: number}>} [availability.stores]
   *   Per-location overrides, keyed by Shopify location id. A `quantity` at or
   *   below the block's low stock threshold shows the low stock label.
   */
  setAvailability({ delivery, clickCollect, inStore, stores } = {}) {
    if (typeof delivery === 'boolean') this.dataset.deliveryAvailable = String(delivery);
    if (typeof clickCollect === 'boolean') this.dataset.ccAvailable = String(clickCollect);
    if (typeof inStore === 'boolean') this.dataset.storeAvailable = String(inStore);

    if (stores) {
      this.#renderStoreListContainers(stores);

      for (const [id, values] of Object.entries(stores)) {
        for (const card of this.querySelectorAll(`[data-store-id="${CSS.escape(id)}"]`)) {
          if (card instanceof HTMLElement) this.#applyStoreValues(card, values);
        }
      }
    }

    this.render();
  }

  #renderStoreListContainers(stores) {
    const selectorList = this.querySelector('[data-selector-list]');
    const instoreList = this.querySelector('[data-instore-list]');
    const compactList = this.querySelector('[data-compact-list]');

    const locations = this.#datajetData?.locations?.edges || [];
    if (!locations.length) return;

    const eligibleLocations = locations.filter(({ node }) => {
      const isWarehouse = node.isWarehouse?.value === 'true';
      const storeHidden = node.storeHidden?.value === 'true';
      return !isWarehouse && !storeHidden;
    });

    const inStockLabel = this.dataset.textInStock || 'In stock';
    const outOfStockLabel = this.dataset.textOutOfStock || 'Out of stock';

    const formatAddress = (loc) => {
      const address = loc.address;
      if (!address) return { line1: '', line2: '' };
      const address1 = (address.address1 || '').trim();
      const address2 = (address.address2 || '').trim();
      const city = (address.city || '').trim();
      const province = (address.provinceCode || address.province || '').trim();
      const zip = (address.zip || '').trim();

      let line2 = address2;
      if (line2 && city) line2 += ', ' + city;
      else if (city) line2 = city;
      if (province) line2 += ' ' + province;
      if (zip) line2 += ' ' + zip;

      return {
        line1: address1,
        line2: line2.trim()
      };
    };

    const escapeHtml = (str) => {
      if (!str) return '';
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    };

    if (selectorList) {
      const name = 'store-availability-store-' + this.id;
      let html = '';

      eligibleLocations.forEach(({ node }) => {
        const id = node.id.replace('gid://shopify/Location/', '');
        const storeInfo = stores[id] || { inStock: false, pickupEnabled: false, quantity: 0, pickupTime: '' };

        const enableForPickup = node.enableForPickup?.value === 'true';
        const isEligible = enableForPickup;

        if (!isEligible) return;

        const storeName = node.storeFrontendName?.value || node.name || '';
        const addr = formatAddress(node);

        const selectable = true;
        const isSelected = id === this.#pendingStoreId;

        const pickupTime = storeInfo.pickupTime || this.dataset.textCcReadyTime || 'Ready in 2 hours';
        const showTime = storeInfo.inStock && pickupTime;

        html += `
          <label
            class="store-availability-card store-availability-card--select${isSelected ? ' store-availability-card--selected' : ''}"
            data-store-card
            data-store-id="${id}"
            data-store-name="${escapeHtml(storeName)}"
            data-store-in-stock="${storeInfo.inStock}"
            data-store-pickup-enabled="true"
            data-store-pickup-time="${escapeHtml(pickupTime)}"
            data-store-selectable="${selectable}"
          >
            <input
              type="radio"
              class="store-availability-card__radio visually-hidden"
              name="${name}"
              value="${id}"
              ${isSelected ? 'checked' : ''}
            >
            <span class="store-availability-card__body">
              <span class="store-availability-card__name body-md">${escapeHtml(storeName)}</span>
              <span class="store-availability-card__address body-sm">
                ${escapeHtml(addr.line1)}
                ${addr.line2 ? ',<br>' + escapeHtml(addr.line2) : ''}
              </span>
              <span class="store-availability-card__status" data-store-status>
                <span
                  class="store-availability-card__status-label ${storeInfo.inStock ? 'store-availability-card__status-label--in-stock' : 'store-availability-card__status-label--out-of-stock'} body-sm-bold"
                  data-store-status-label
                >
                  ${storeInfo.inStock ? inStockLabel : outOfStockLabel}
                </span>
                ${showTime ? `
                  <span class="store-availability-card__status-separator" aria-hidden="true"></span>
                  <span class="store-availability-card__status-time body-sm-bold" data-store-status-time>${escapeHtml(pickupTime)}</span>
                ` : `
                  <span class="store-availability-card__status-separator" aria-hidden="true" hidden></span>
                  <span class="store-availability-card__status-time body-sm" data-store-status-time hidden></span>
                `}
              </span>
            </span>
          </label>
        `;
      });
      selectorList.innerHTML = html;
    }

    if (instoreList) {
      let html = '';

      eligibleLocations.forEach(({ node }) => {
        const storeStockAvailability = node.storeStockAvailability?.value === 'true';
        if (!storeStockAvailability) return;

        const id = node.id.replace('gid://shopify/Location/', '');
        const storeInfo = stores[id] || { inStock: false, pickupEnabled: false, quantity: 0, pickupTime: '' };

        const storeName = node.storeFrontendName?.value || node.name || '';
        const addr = formatAddress(node);

        const pickupTime = storeInfo.pickupTime || this.dataset.textCcReadyTime || 'Ready in 2 hours';
        const showTime = storeInfo.inStock && storeInfo.pickupEnabled && pickupTime;

        html += `
          <div
            class="store-availability-card store-availability-card--readonly"
            data-store-card
            data-store-id="${id}"
            data-store-name="${escapeHtml(storeName)}"
            data-store-in-stock="${storeInfo.inStock}"
            data-store-pickup-enabled="${storeInfo.pickupEnabled}"
            data-store-pickup-time="${escapeHtml(pickupTime)}"
          >
            <span class="store-availability-card__body">
              <span class="store-availability-card__name body-md-bold">${escapeHtml(storeName)}</span>
              <span class="store-availability-card__address body-sm">
                ${escapeHtml(addr.line1)}
                ${addr.line2 ? ',<br>' + escapeHtml(addr.line2) : ''}
              </span>
              <span class="store-availability-card__status" data-store-status>
                <span
                  class="store-availability-card__status-label ${storeInfo.inStock ? 'store-availability-card__status-label--in-stock' : 'store-availability-card__status-label--out-of-stock'} body-sm-bold"
                  data-store-status-label
                >
                  ${storeInfo.inStock ? inStockLabel : outOfStockLabel}
                </span>
                ${showTime ? `
                  <span class="store-availability-card__status-separator" aria-hidden="true"></span>
                  <span class="store-availability-card__status-time body-sm-bold" data-store-status-time>${escapeHtml(pickupTime)}</span>
                ` : `
                  <span class="store-availability-card__status-separator" aria-hidden="true" hidden></span>
                  <span class="store-availability-card__status-time body-sm" data-store-status-time hidden></span>
                `}
              </span>
            </span>
          </div>
        `;
      });
      instoreList.innerHTML = html;
    }

    if (compactList) {
      let html = '';

      eligibleLocations.forEach(({ node }) => {
        const storeStockAvailability = node.storeStockAvailability?.value === 'true';
        if (!storeStockAvailability) return;

        const id = node.id.replace('gid://shopify/Location/', '');
        const storeInfo = stores[id] || { inStock: false, pickupEnabled: false, quantity: 0, pickupTime: '' };

        const storeName = node.storeFrontendName?.value || node.name || '';
        const addr = formatAddress(node);

        const pickupTime = storeInfo.pickupTime || this.dataset.textCcReadyTime || 'Ready in 2 hours';
        const showTime = storeInfo.inStock && storeInfo.pickupEnabled && pickupTime;

        const directionsQuery = encodeURIComponent(`${storeName} ${addr.line1} ${addr.line2}`);
        const directionsUrl = `https://www.google.com/maps/search/?api=1&query=${directionsQuery}`;
        const directionsLabel = this.dataset.textDirectionsLabel || 'Get directions';

        html += `
          <div
            class="store-availability-card store-availability-card--compact"
            data-store-card
            data-store-id="${id}"
            data-store-name="${escapeHtml(storeName)}"
            data-store-in-stock="${storeInfo.inStock}"
            data-store-pickup-enabled="${storeInfo.pickupEnabled}"
            data-store-pickup-time="${escapeHtml(pickupTime)}"
          >
            <span
              class="store-availability-card__icon store-availability-card__icon--in-stock svg-wrapper"
              data-store-icon="in-stock"
              ${storeInfo.inStock ? '' : 'hidden'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                <g clip-path="url(#clip0_25210_9897)">
                  <path d="M7.5 13.75C10.9518 13.75 13.75 10.9518 13.75 7.5C13.75 4.04822 10.9518 1.25 7.5 1.25C4.04822 1.25 1.25 4.04822 1.25 7.5C1.25 10.9518 4.04822 13.75 7.5 13.75Z" stroke="#008000" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M5.625 7.5L6.875 8.75L9.375 6.25" stroke="#008000" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip0_25210_9897">
                    <rect width="15" height="15" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            </span>
            <span
              class="store-availability-card__icon store-availability-card__icon--out-of-stock svg-wrapper"
              data-store-icon="out-of-stock"
              ${storeInfo.inStock ? 'hidden' : ''}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                <g clip-path="url(#clip0_25210_9921)">
                  <path d="M7.5 13.75C10.9518 13.75 13.75 10.9518 13.75 7.5C13.75 4.04822 10.9518 1.25 7.5 1.25C4.04822 1.25 1.25 4.04822 1.25 7.5C1.25 10.9518 4.04822 13.75 7.5 13.75Z" stroke="#C8102E" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M9.375 5.625L5.625 9.375" stroke="#C8102E" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M5.625 5.625L9.375 9.375" stroke="#C8102E" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip0_25210_9921">
                    <rect width="15" height="15" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            </span>
            <span class="store-availability-card__body">
              <span class="store-availability-card__name body-md">${escapeHtml(storeName)}</span>
              <span class="store-availability-card__status" data-store-status>
                <span
                  class="store-availability-card__status-label ${storeInfo.inStock ? 'store-availability-card__status-label--in-stock' : 'store-availability-card__status-label--out-of-stock'} body-sm-bold"
                  data-store-status-label
                >
                  ${storeInfo.inStock ? inStockLabel : outOfStockLabel}
                </span>
                ${showTime ? `
                  <span class="store-availability-card__status-separator" aria-hidden="true"></span>
                  <span class="store-availability-card__status-time body-sm-bold" data-store-status-time>${escapeHtml(pickupTime)}</span>
                ` : `
                  <span class="store-availability-card__status-separator" aria-hidden="true" hidden></span>
                  <span class="store-availability-card__status-time body-sm" data-store-status-time hidden></span>
                `}
              </span>
            </span>
            <a
              class="store-availability-card__directions body-sm"
              href="${directionsUrl}"
              target="_blank"
              rel="noopener"
            >
              ${directionsLabel}
            </a>
          </div>
        `;
      });
      compactList.innerHTML = html;
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {{inStock?: boolean, pickupEnabled?: boolean, pickupTime?: string, quantity?: number}} values
   */
  #applyStoreValues(card, values) {
    if (typeof values.quantity === 'number') {
      card.dataset.storeQuantity = String(values.quantity);
      if (typeof values.inStock !== 'boolean') values.inStock = values.quantity > 0;
    }
    if (typeof values.inStock === 'boolean') card.dataset.storeInStock = String(values.inStock);
    if (typeof values.pickupEnabled === 'boolean') card.dataset.storePickupEnabled = String(values.pickupEnabled);
    if (typeof values.pickupTime === 'string') card.dataset.storePickupTime = values.pickupTime;

    const inStock = card.dataset.storeInStock === 'true';
    const pickupEnabled = card.dataset.storePickupEnabled === 'true';
    const pickupTime = card.dataset.storePickupTime ?? '';

    const threshold = Number(this.dataset.lowStockThreshold);
    const quantity = Number(card.dataset.storeQuantity);
    const isLow =
      inStock && Number.isFinite(threshold) && threshold > 0 && Number.isFinite(quantity) && quantity <= threshold;

    const label = card.querySelector('[data-store-status-label]');
    if (label instanceof HTMLElement) {
      label.textContent = inStock
        ? isLow
          ? this.dataset.textLowStock || this.dataset.textInStock || ''
          : this.dataset.textInStock ?? ''
        : this.dataset.textOutOfStock ?? '';
      label.classList.toggle('store-availability-card__status-label--in-stock', inStock && !isLow);
      label.classList.toggle('store-availability-card__status-label--low-stock', isLow);
      label.classList.toggle('store-availability-card__status-label--out-of-stock', !inStock);
    }

    const showTime = Boolean(inStock && pickupEnabled && pickupTime);
    const time = card.querySelector('[data-store-status-time]');
    if (time instanceof HTMLElement) time.textContent = pickupTime;
    this.#toggle(time, showTime);
    this.#toggle(card.querySelector('.store-availability-card__status-separator'), showTime);

    this.#toggle(card.querySelector('[data-store-icon="in-stock"]'), inStock);
    this.#toggle(card.querySelector('[data-store-icon="out-of-stock"]'), !inStock);

    if (card.classList.contains('store-availability-card--select')) {
      const selectable = true;
      card.dataset.storeSelectable = String(selectable);
      card.classList.toggle('store-availability-card--disabled', !selectable);
      const input = card.querySelector('input[type="radio"]');
      if (input instanceof HTMLInputElement) input.disabled = !selectable;
    }
  }

  /**
   * Store card for the remembered store, if it is still rendered for this variant.
   * @returns {HTMLElement | null}
   */
  #selectedStoreCard() {
    if (!this.#selectedStore) return null;
    const card = this.querySelector(
      `[data-selector-list] [data-store-id="${CSS.escape(this.#selectedStore.id)}"]`
    );
    return card instanceof HTMLElement ? card : null;
  }

  /* ------------------------------------------------------------------ *
   * Rendering
   * ------------------------------------------------------------------ */

  render() {
    const view = this.#view;

    this.#toggle(this.refs.viewMain, view === 'main');
    this.#toggle(this.refs.viewInStoreOnly, view === 'in-store-only');
    this.#toggle(this.refs.viewOutOfStock, view === 'out-of-stock');

    if (view === 'main') {
      this.#renderTabs();
      this.#renderClickCollectPanel();
    }

    this.#syncAddToCartButton(view);
  }

  #renderTabs() {
    const { tabs } = this.refs;
    if (!tabs) return;

    // A channel with no stock is struck through and cannot be opened.
    const enabled = {
      delivery: this.#deliveryAvailable && this.#hasTab('delivery'),
      'click-collect': this.#ccAvailable && this.#hasTab('click-collect'),
    };

    if (!enabled[this.#activeTab]) {
      this.#activeTab = enabled.delivery ? 'delivery' : 'click-collect';
    }

    for (const tab of tabs.querySelectorAll('[data-tab]')) {
      if (!(tab instanceof HTMLElement)) continue;

      const name = /** @type {'delivery' | 'click-collect'} */ (tab.dataset.tab);
      const isEnabled = Boolean(enabled[name]);
      const isActive = isEnabled && name === this.#activeTab;

      tab.classList.toggle('store-availability__tab--active', isActive);
      tab.classList.toggle('store-availability__tab--disabled', !isEnabled);
      tab.setAttribute('aria-selected', String(isActive));
      tab.toggleAttribute('disabled', !isEnabled);

      const subtitle = tab.querySelector('[data-tab-subtitle]');
      if (subtitle) {
        subtitle.textContent = isEnabled
          ? name === 'delivery'
            ? this.dataset.textDeliveryLeadTime ?? ''
            : this.dataset.textCcReadyTime ?? ''
          : this.dataset.textTabOutOfStock ?? '';
      }

      this.#toggle(this.querySelector(`[data-panel="${name}"]`), isActive);
    }
  }

  #renderClickCollectPanel() {
    const { ccHeading, ccNote, ccAction, ccActionIcon, ccActionLabel, ccActionChevron } = this.refs;
    const { ccIconEmpty, ccIconInStock, ccIconOutOfStock } = this.refs;
    if (!ccHeading || !ccNote || !ccAction) return;

    const card = this.#selectedStoreCard();
    const defaults = this.#captureDefaults();

    if (!this.#selectedStore || !card) {
      this.#toggle(ccIconEmpty, true);
      this.#toggle(ccIconInStock, false);
      this.#toggle(ccIconOutOfStock, false);

      ccHeading.textContent = defaults.ccEmptyHeading;
      ccNote.textContent = defaults.ccEmptyNote;
      ccNote.classList.remove('store-availability__panel-note--error');

      if (ccActionLabel) ccActionLabel.textContent = defaults.selectStoreLabel;
      this.#toggle(ccActionIcon, false);
      this.#toggle(ccActionChevron, true);
      ccAction.classList.add('store-availability__panel-action--accent');
      return;
    }

    const inStock = card.dataset.storeInStock === 'true' && card.dataset.storePickupEnabled === 'true';
    const pickupTime = card.dataset.storePickupTime ?? '';

    this.#toggle(ccIconEmpty, false);
    this.#toggle(ccIconInStock, inStock);
    this.#toggle(ccIconOutOfStock, !inStock);

    ccHeading.textContent = this.#selectedStore.name;

    if (inStock) {
      ccNote.classList.remove('store-availability__panel-note--error');
      ccNote.replaceChildren(this.#statusFragment(this.dataset.textInStock ?? '', pickupTime));

      if (ccActionLabel) ccActionLabel.textContent = this.dataset.textChangeStore ?? '';
      this.#toggle(ccActionIcon, true);
      this.#toggle(ccActionChevron, false);
      ccAction.classList.remove('store-availability__panel-action--accent');
    } else {
      ccNote.classList.add('store-availability__panel-note--error');
      ccNote.textContent = this.dataset.textOutOfStock ?? '';

      if (ccActionLabel) ccActionLabel.textContent = this.dataset.textTryAnotherStore ?? '';
      this.#toggle(ccActionIcon, false);
      this.#toggle(ccActionChevron, true);
      ccAction.classList.add('store-availability__panel-action--accent');
    }
  }

  /**
   * Builds `In stock · Ready in 1 hour`.
   *
   * @param {string} statusLabel
   * @param {string} pickupTime
   * @returns {DocumentFragment}
   */
  #statusFragment(statusLabel, pickupTime) {
    const fragment = document.createDocumentFragment();

    const status = document.createElement('span');
    status.className = 'store-availability__panel-status body-sm-bold';
    status.textContent = statusLabel;
    fragment.append(status);

    if (pickupTime) {
      const separator = document.createElement('span');
      separator.className = 'store-availability__panel-separator';
      separator.setAttribute('aria-hidden', 'true');
      separator.textContent = '·';

      const time = document.createElement('span');
      time.textContent = pickupTime;

      fragment.append(separator, time);
    }

    return fragment;
  }

  /**
   * Liquid always renders the "no store chosen" copy, so it is captured before
   * the first paint and reused whenever the panel returns to that state.
   */
  #captureDefaults() {
    if (!this.#defaultCopy) {
      this.#defaultCopy = {
        ccEmptyHeading: this.refs.ccHeading?.textContent?.trim() ?? '',
        ccEmptyNote: this.refs.ccNote?.textContent?.trim() ?? '',
        selectStoreLabel: this.refs.ccActionLabel?.textContent?.trim() ?? '',
      };
    }
    return this.#defaultCopy;
  }

  /**
   * @param {Element | null | undefined} element
   * @param {boolean} visible
   */
  #toggle(element, visible) {
    if (element instanceof HTMLElement) element.toggleAttribute('hidden', !visible);
  }

  /* ------------------------------------------------------------------ *
   * Tabs
   * ------------------------------------------------------------------ */

  /**
   * @param {Event} event
   */
  handleTabClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest('[data-tab]');
    if (!(tab instanceof HTMLElement) || tab.hasAttribute('disabled')) return;

    this.#activeTab = /** @type {'delivery' | 'click-collect'} */ (tab.dataset.tab);
    this.render();
  }

  /* ------------------------------------------------------------------ *
   * Drawers
   * ------------------------------------------------------------------ */

  openStoreSelector() {
    const drawer = this.refs.storeSelector;
    if (!drawer) return;

    this.#pendingStoreId = this.#selectedStore?.id ?? null;
    this.#syncSelectorList();
    this.#updateSelectorFooter();

    /** @type {any} */ (drawer).showDialog?.();
  }

  openInStoreDrawer() {
    /** @type {any} */ (this.refs.inStoreDrawer)?.showDialog?.();
  }

  #bindSelectorList() {
    const list = this.querySelector('[data-selector-list]');
    if (!(list instanceof HTMLElement) || list.dataset.bound === 'true') return;

    list.dataset.bound = 'true';
    list.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

      this.#pendingStoreId = input.value;
      this.#syncSelectorList();
      this.#updateSelectorFooter();
    });
  }

  #syncSelectorList() {
    for (const card of this.querySelectorAll('[data-selector-list] [data-store-card]')) {
      if (!(card instanceof HTMLElement)) continue;

      const isSelected = card.dataset.storeId === this.#pendingStoreId && card.dataset.storeSelectable === 'true';
      card.classList.toggle('store-availability-card--selected', isSelected);

      const input = card.querySelector('input[type="radio"]');
      if (input instanceof HTMLInputElement) input.checked = isSelected;
    }
  }

  async #updateSelectorFooter() {
    const hint = this.querySelector('[data-selector-hint]');
    const confirmation = this.querySelector('[data-selector-confirmation]');
    const confirmationText = this.querySelector('[data-selector-confirmation-text]');
    const warning = this.querySelector('[data-selector-warning]');
    const warningNote = this.querySelector('[data-selector-warning-note]');
    const confirm = this.querySelector('[data-selector-confirm]');

    const card = this.#pendingCard();

    if (!card) {
      this.#toggle(hint, true);
      this.#toggle(confirmation, false);
      this.#toggle(warning, false);
      this.#toggle(confirm, false);
      return;
    }

    const storeName = card.dataset.storeName ?? '';

    this.#toggle(hint, false);
    this.#toggle(confirm, true);

    // A cart or PDP selection already reserved against another store has to be moved, so warn
    // before the customer commits to the change.
    const cartStore = await this.#cartPickupStore();

    // The selection may have changed while the cart request was in flight.
    if (this.#pendingCard()?.dataset.storeId !== card.dataset.storeId) return;

    const currentStore = this.#selectedStore?.name || cartStore;
    const isMove = Boolean(currentStore) && currentStore !== storeName;

    this.#toggle(warning, isMove);
    this.#toggle(confirmation, !isMove);

    if (isMove && warningNote instanceof HTMLElement) {
      warningNote.textContent = (this.dataset.textChangeStoreWarning ?? '').replace('[store]', currentStore ?? '');
    }

    if (!isMove && confirmationText instanceof HTMLElement) {
      confirmationText.textContent = `${this.dataset.textCollectingFrom ?? ''} ${storeName}`.trim();
    }
  }

  /**
   * @returns {HTMLElement | null}
   */
  #pendingCard() {
    if (!this.#pendingStoreId) return null;
    const card = this.querySelector(
      `[data-selector-list] [data-store-id="${CSS.escape(this.#pendingStoreId)}"]`
    );
    return card instanceof HTMLElement && card.dataset.storeSelectable === 'true' ? card : null;
  }

  async confirmStore() {
    const card = this.#pendingCard();
    if (!card) return;

    this.#selectedStore = {
      id: card.dataset.storeId ?? '',
      name: card.dataset.storeName ?? '',
    };
    writeSelectedStore(this.#selectedStore);

    this.#activeTab = 'click-collect';

    await this.#updateCartAttributes(this.#selectedStore);

    /** @type {any} */ (this.refs.storeSelector)?.closeDialog?.();
    this.render();
  }

  /* ------------------------------------------------------------------ *
   * Cart helpers
   * ------------------------------------------------------------------ */

  /**
   * @returns {Promise<string | null>}
   */
  async #cartPickupStore() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      if (!response.ok) return null;
      const cart = await response.json();
      if (!cart || cart.item_count === 0) return null;
      return cart.attributes?.[CART_ATTRIBUTE] || null;
    } catch {
      return null;
    }
  }

  /**
   * Writes the pickup store name and its numeric Shopify location id (the value
   * the Base theme sends as `_pickup_store_id`) in one request, so they can't drift.
   * @param {SelectedStore} store
   */
  async #updateCartAttributes(store) {
    try {
      await fetch(`${Theme.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            [CART_ATTRIBUTE]: store.name || '',
            [CART_ATTRIBUTE_STORE_ID]: store.id || '',
          },
        }),
      });
    } catch {
      // A failed attribute write must not block the store selection.
    }
  }

  /* ------------------------------------------------------------------ *
   * Add to bag button
   * ------------------------------------------------------------------ */

  /**
   * @returns {HTMLButtonElement | null}
   */
  #addToCartButton() {
    const scope = this.#section ?? document;
    const button = scope.querySelector('button[data-testid="standalone-add-to-cart"]');
    return button instanceof HTMLButtonElement ? button : null;
  }

  /**
   * @param {'main' | 'in-store-only' | 'out-of-stock'} view
   */
  #syncAddToCartButton(view) {
    if (this.dataset.syncAddToCart !== 'true') return;

    const button = this.#addToCartButton();
    if (!button) return;

    let label = null;
    let forceDisabled = false;
    let needsStore = false;

    if (view === 'in-store-only') {
      label = this.dataset.textAtcInStoreOnly || 'Available in store only';
      forceDisabled = true;
    } else if (view === 'out-of-stock') {
      const isWillBeRestocked = this.dataset.willBeRestocked === 'true';
      if (isWillBeRestocked) {
        label = 'Notify me';
        button.dataset.isNotifyMe = 'true';
      } else {
        label = this.dataset.textAtcOutOfStock || 'Sold out';
        button.dataset.isNotifyMe = 'false';
      }
      forceDisabled = true;
    } else if (this.#activeTab === 'click-collect') {
      const card = this.#selectedStoreCard();
      const hasStock = card?.dataset.storeInStock === 'true' && card?.dataset.storePickupEnabled === 'true';
      label = hasStock
        ? (this.dataset.textAtcCollect || 'Collect in store')
        : (this.dataset.textAtcChooseStore || 'Choose a store');
      needsStore = !hasStock;
    }

    if (!label) {
      this.#restoreAddToCartButton();
      return;
    }

    const text = button.querySelector('.add-to-cart-text__content span span');
    if (!(text instanceof HTMLElement)) return;

    if (button.dataset.storeAvailabilityManaged !== 'true') {
      button.dataset.storeAvailabilityManaged = 'true';
      button.dataset.storeAvailabilityLabel = text.textContent ?? '';
      button.dataset.storeAvailabilityDisabled = button.disabled ? 'true' : 'false';
    }

    const wasDisabled = button.dataset.storeAvailabilityDisabled === 'true';
    const originalLabel = (button.dataset.storeAvailabilityLabel || '').trim().toLowerCase();
    const isMakeSelection = this.dataset.hasSelectedVariant === 'false' || originalLabel === 'make a selection' || originalLabel.includes('make a selection');

    // The theme already disables the button for its own reasons — most often
    // "Make a selection" before a size is picked. Relabelling it there would
    // hide the real blocker, so the fulfilment label is left alone when no variant is chosen.
    if (isMakeSelection || (wasDisabled && view === 'main')) {
      this.#restoreAddToCartButton();
      return;
    }

    text.textContent = label;

    // Show/hide icon depending on label
    const icon = button.querySelector('.add-to-cart-icon');
    if (icon) {
      const showIcon = label.trim().toLowerCase().startsWith('add to bag');
      icon.style.display = showIcon ? 'inline-flex' : 'none';
    }
    const notifyIcon = button.querySelector('.notify-me-icon');
    if (notifyIcon) {
      const showNotify = label.trim().toLowerCase().startsWith('notify');
      notifyIcon.style.display = showNotify ? 'inline-flex' : 'none';
    }

    button.dataset.storeAvailabilityBlock = this.id;
    // Only intercept the submit while the customer still has to pick a store.
    button.dataset.storeAvailabilityNeedsStore = String(needsStore && !wasDisabled);
    button.disabled = forceDisabled || wasDisabled;
  }

  /**
   * Drops the remembered "original" add-to-cart label/disabled snapshot
   * without touching the button itself. The snapshot is captured lazily,
   * the first time `#syncAddToCartButton` needs to override the button for
   * a given variant, so it can be put back once this block has nothing to
   * say (e.g. the delivery tab is available and no special label applies).
   * Without invalidating it on every variant change, the snapshot keeps
   * describing whichever variant happened to be selected the very first
   * time it was captured (often the sold-out variant selected on page
   * load), so `#restoreAddToCartButton` would later reapply that stale
   * "Sold out" / disabled state onto a newly selected, available variant.
   */
  #invalidateAddToCartSnapshot() {
    const button = this.#addToCartButton();
    if (!button) return;
    delete button.dataset.storeAvailabilityManaged;
    delete button.dataset.storeAvailabilityLabel;
    delete button.dataset.storeAvailabilityDisabled;
    delete button.dataset.storeAvailabilityNeedsStore;
    delete button.dataset.storeAvailabilityBlock;
  }

  #restoreAddToCartButton() {
    const button = this.#addToCartButton();
    if (!button || button.dataset.storeAvailabilityManaged !== 'true') return;

    const text = button.querySelector('.add-to-cart-text__content span span');
    if (text instanceof HTMLElement) text.textContent = button.dataset.storeAvailabilityLabel ?? '';

    // Restore icon
    const icon = button.querySelector('.add-to-cart-icon');
    if (icon) {
      const originalText = button.dataset.storeAvailabilityLabel ?? '';
      const showIcon = originalText.trim().toLowerCase().startsWith('add to bag');
      icon.style.display = showIcon ? 'inline-flex' : 'none';
    }
    const notifyIcon = button.querySelector('.notify-me-icon');
    if (notifyIcon) {
      const originalText = button.dataset.storeAvailabilityLabel ?? '';
      const showNotify = originalText.trim().toLowerCase().startsWith('notify');
      notifyIcon.style.display = showNotify ? 'inline-flex' : 'none';
    }

    button.disabled = button.dataset.storeAvailabilityDisabled === 'true';
    delete button.dataset.storeAvailabilityManaged;
    delete button.dataset.storeAvailabilityLabel;
    delete button.dataset.storeAvailabilityDisabled;
    delete button.dataset.storeAvailabilityNeedsStore;
    delete button.dataset.storeAvailabilityBlock;
  }

  /* ------------------------------------------------------------------ *
   * Variant changes
   * ------------------------------------------------------------------ */

  /**
   * Availability is variant specific, so pull a fresh render of the block and
   * morph it in — the same approach the theme uses for inventory and pickup.
   *
   * @param {Event} event
   */
  #handleVariantSelected = (event) => {
    const optionValueId = event.detail?.resource?.id;
    const selectedRadio = this.closest('.shopify-section')?.querySelector(`input[data-option-value-id="${optionValueId}"]`);
    const variantId = selectedRadio?.dataset.variantId;
    if (variantId) {
      this.dataset.hasSelectedVariant = 'true';
      if (this.dataset.variantId !== String(variantId)) {
        this.#invalidateAddToCartSnapshot();
      }
      if (this.#datajetData) {
        this.dataset.variantId = String(variantId);
        this.#applyDatajetStock();
      }
    }
  };

  #handleVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;

    if (detail?.data?.newProduct?.url) {
      this.dataset.productUrl = detail.data.newProduct.url;
    }

    const variantId = detail?.resource?.id;
    if (!variantId) return;

    if (this.dataset.variantId !== String(variantId)) {
      this.#invalidateAddToCartSnapshot();
    }

    this.dataset.hasSelectedVariant = 'true';
    this.dataset.variantId = String(variantId);

    // Apply cached stock immediately so button never flickers
    if (this.#datajetData) {
      this.#applyDatajetStock();
      setTimeout(() => {
        this.#applyDatajetStock();
      }, 0);
    }

    this.#fetchVariant(String(variantId));
  };

  /**
   * @param {string} variantId
   */
  #fetchVariant(variantId) {
    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    const url = `${this.dataset.productUrl}?variant=${variantId}&section_id=${this.dataset.sectionId}`;

    fetch(url, { signal: controller.signal })
      .then((response) => response.text())
      .then((text) => {
        if (controller.signal.aborted) return;

        const html = new DOMParser().parseFromString(text, 'text/html');
        const next = html.getElementById(this.id);
        if (!(next instanceof HTMLElement) || next.tagName.toLowerCase() !== 'store-availability') return;

        // Availability flags live on the host element, which morph leaves alone.
        for (const name of [
          'variantId',
          'deliveryAvailable',
          'ccAvailable',
          'storeAvailable',
          'hasDeliveryTab',
          'hasCcTab',
        ]) {
          const value = next.dataset[name];
          if (value != null) this.dataset[name] = value;
        }

        // Morphing an open drawer would strip its `open` attribute, so close
        // any drawer first. Changing variant behind a modal is not reachable
        // by pointer, but section rendering can still land here.
        for (const drawer of [this.refs.storeSelector, this.refs.inStoreDrawer]) {
          /** @type {any} */ (drawer)?.closeDialog?.();
        }

        morph(this, next, { childrenOnly: true });

        this.#refresh();
      })
      .catch(() => {
        // Leave the previously rendered state in place on a failed fetch.
      });
  }
}

if (!customElements.get('store-availability')) {
  customElements.define('store-availability', StoreAvailability);
}
